import unwrapBundlerHtml from "./unwrapBundlerHtml";
import {
  imageFilesFromDataTransfer,
  isImageFile,
  takeImageFiles,
  uploadLocalImageFile,
} from "./aiChatImageDrop";

const looksLikeCsv = (text) => {
  const lines = text.trim().split("\n");
  return lines.length > 1 && lines.every((line) => line.split(",").length > 1);
};

const stripMarkdownCodeFence = (text) => {
  const match = String(text || "").match(/^```[a-z]*\s*\n([\s\S]*?)\n```\s*$/i);
  return match ? match[1].trim() : text;
};

export const looksLikeImportHtml = (text) => {
  const body = stripMarkdownCodeFence(String(text || "").trim());
  if (body.length < 400) return false;
  if (/^(<!doctype\s+html|<html[\s>])/i.test(body)) return true;
  if (!/<[a-z][\w-]*[\s>]/i.test(body)) return false;

  const hasBlockStructure =
    /<\/(div|section|main|form|body|article|table|ul|ol)>/i.test(body);
  if (!hasBlockStructure) return false;

  const hasStyleOrScript = /<(style|script)[\s>]/i.test(body);
  const openingTagCount = (body.match(/<[a-z][\w-]*[\s>]/gi) || []).length;
  return hasStyleOrScript || openingTagCount >= 10;
};

export const looksLikeMarkdownDocument = (text) => {
  const body = String(text || "").trim();
  if (body.length < 400) return false;
  if (looksLikeImportHtml(body)) return false;
  if (/^```(?:md|markdown)\s*\n/i.test(body)) return true;

  const headingCount = (body.match(/^#{1,6}\s+\S/gm) || []).length;
  if (headingCount >= 2) return true;
  return /^#\s+\S/m.test(body) && /^[-*]\s+\S/m.test(body);
};

export const htmlAttachmentFromPaste = (text) => ({
  type: "html",
  content: unwrapBundlerHtml(stripMarkdownCodeFence(String(text || "").trim())),
  name: "Pasted HTML",
});

export const markdownAttachmentFromPaste = (text) => ({
  type: "markdown",
  content: String(text || "").trim(),
  name: "Pasted Markdown",
});

export const attachmentFromPastedText = (text) => {
  const raw = String(text || "");
  if (!raw) return null;

  if (looksLikeImportHtml(raw)) return htmlAttachmentFromPaste(raw);
  if (looksLikeMarkdownDocument(raw)) return markdownAttachmentFromPaste(raw);
  if (raw.length >= 400 && looksLikeCsv(raw)) {
    return { type: "csv", content: raw, name: "Pasted CSV" };
  }
  return null;
};

export const clipboardTextCandidates = (clipboardData) => {
  if (!clipboardData || typeof clipboardData.getData !== "function") return [];

  const seen = new Set();
  const candidates = [];
  ["text/plain", "text", "text/html"].forEach((type) => {
    let value = "";
    try {
      value = clipboardData.getData(type);
    } catch (_error) {
      value = "";
    }
    const text = String(value || "");
    if (!text || seen.has(text)) return;
    seen.add(text);
    candidates.push({ type, text });
  });
  return candidates;
};

export const attachmentFromClipboard = (clipboardData) => {
  for (const { text } of clipboardTextCandidates(clipboardData)) {
    const attachment = attachmentFromPastedText(text);
    if (attachment) return attachment;
  }
  return null;
};

export const readClipboardFileText = (file) => {
  if (file && typeof file.text === "function") return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
};

export const isAiImportTextFile = (file) => {
  const name = String(file?.name || "").toLowerCase();
  const type = String(file?.type || "").toLowerCase();
  return (
    name.endsWith(".html") ||
    name.endsWith(".htm") ||
    name.endsWith(".md") ||
    name.endsWith(".markdown") ||
    type === "text/html" ||
    type === "text/markdown"
  );
};

export { isImageFile, imageFilesFromDataTransfer };

export const htmlOrMarkdownFilesFromDataTransfer = (dataTransfer) => {
  if (!dataTransfer) return [];
  return Array.from(dataTransfer.files || []).filter(isAiImportTextFile);
};

export const preventFileDrag = (event) => {
  const types = event.dataTransfer && event.dataTransfer.types;
  if (!types || !Array.from(types).includes("Files")) return;
  event.preventDefault();
  event.dataTransfer.dropEffect = "copy";
};

export const uploadReferenceImageFile = (file, payload) => {
  const client = typeof window !== "undefined" ? window.uploads : undefined;
  const notifyError = (error) => {
    if (!payload || typeof payload.onError !== "function") return;
    if (error && error.uploadErrorNotified) return;
    if (error && typeof error === "object") error.uploadErrorNotified = true;
    payload.onError(error);
  };
  if (!client || typeof client.storeFile !== "function") {
    notifyError(new Error("Image upload is unavailable"));
    return Promise.resolve();
  }
  try {
    return Promise.resolve(client.storeFile(file, payload)).catch((error) => {
      notifyError(error);
    });
  } catch (error) {
    notifyError(error);
    return Promise.resolve();
  }
};

export const MAX_IMPORTED_ATTACHMENTS = 5;

const textAttachmentFromFile = (file, raw) => {
  const isMarkdown =
    /\.(md|markdown)$/i.test(file.name || "") ||
    String(file.type || "").toLowerCase() === "text/markdown";
  return {
    type: isMarkdown ? "markdown" : "html",
    content: isMarkdown ? raw : unwrapBundlerHtml(raw),
    name: file.name,
  };
};

export const attachImportedAssetsFromEvent = (
  event,
  {
    addAttachment,
    website,
    environment,
    attachmentCount = 0,
    maxAttachments = MAX_IMPORTED_ATTACHMENTS,
    pendingUploadsRef,
    onPendingUploadsChange,
    onLimitMessage,
    forceNsfwCheck = true,
  }
) => {
  const transfer = event.clipboardData || event.dataTransfer;
  const allFiles = Array.from((transfer && transfer.files) || []);
  const textFiles = allFiles.filter(isAiImportTextFile);
  const textAttachment = textFiles.length
    ? null
    : attachmentFromClipboard(event.clipboardData);

  if (!allFiles.length && !textAttachment) return false;

  event.preventDefault();
  const pendingUploads =
    pendingUploadsRef && Number.isFinite(pendingUploadsRef.current)
      ? Math.max(0, pendingUploadsRef.current)
      : 0;
  const remainingSlots = Math.max(
    0,
    maxAttachments - attachmentCount - pendingUploads
  );
  const textFilesToRead = textFiles.slice(0, remainingSlots);
  const reservedTextSlots =
    textFilesToRead.length +
    (textAttachment && remainingSlots > textFilesToRead.length ? 1 : 0);
  const remainingImageSlots = Math.max(0, remainingSlots - reservedTextSlots);
  const { accepted, omitted, oversized, unsupported } = takeImageFiles(
    allFiles,
    remainingImageSlots
  );
  const otherUnsupported = Math.max(0, unsupported - textFiles.length);
  const limitParts = [];
  if (otherUnsupported > 0) {
    limitParts.push("Only JPG, PNG, GIF or WebP images can be attached.");
  }
  if (oversized > 0) limitParts.push("Images must be 10MB or smaller.");
  if (omitted > 0 || textFiles.length > textFilesToRead.length) {
    limitParts.push(`You can attach up to ${maxAttachments} attachments.`);
  } else if (textAttachment && remainingSlots <= textFilesToRead.length) {
    limitParts.push(`You can attach up to ${maxAttachments} attachments.`);
  }
  const limitMessage = [...new Set(limitParts)].join(" ");
  if (onLimitMessage) onLimitMessage(limitMessage);

  const bumpPending = (delta) => {
    if (pendingUploadsRef) {
      pendingUploadsRef.current = Math.max(
        0,
        (pendingUploadsRef.current | 0) + delta
      );
    }
    if (onPendingUploadsChange) {
      onPendingUploadsChange(
        pendingUploadsRef ? pendingUploadsRef.current : undefined
      );
    }
  };

  const websiteId = website && website.id;
  const env = (environment || "production").toLowerCase();
  const path = `/websites/${websiteId}/`;

  if (accepted.length && !websiteId) {
    if (onLimitMessage) {
      onLimitMessage("Unable to attach that image. Try the + button.");
    }
  } else if (accepted.length) {
    bumpPending(accepted.length);
    accepted.forEach((file) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        bumpPending(-1);
      };
      const runStoreFile = () =>
        uploadReferenceImageFile(file, {
          environment: env,
          path,
          website_id: websiteId,
          forceNsfwCheck,
          skipProcessImage: true,
          library: true,
          callback: (fileUrl, fileMeta = {}) => {
            try {
              addAttachment({
                type: "image",
                url: fileUrl,
                name: fileMeta.filename || file.name,
              });
            } finally {
              finish();
            }
          },
          onError: finish,
        });

      const uploads = typeof window !== "undefined" ? window.uploads : null;
      const filestack = typeof window !== "undefined" ? window.filestack : null;
      if (
        uploads &&
        typeof uploads.transform === "function" &&
        filestack &&
        typeof filestack.init === "function"
      ) {
        uploadLocalImageFile({
          file,
          environment: env,
          path,
          websiteId,
          forceNsfwCheck,
          skipProcessImage: true,
          library: true,
          callback: (fileUrl, fileMeta = {}) => {
            try {
              addAttachment({
                type: "image",
                url: fileUrl,
                name:
                  (fileMeta &&
                    (fileMeta.filename || fileMeta.cf_original_filename)) ||
                  file.name,
              });
            } finally {
              finish();
            }
          },
        }).catch(() => {
          finish();
        });
        return;
      }
      runStoreFile();
    });
  }

  if (textFilesToRead.length) bumpPending(textFilesToRead.length);
  textFilesToRead.forEach((file) => {
    const finishRead = () => bumpPending(-1);
    Promise.resolve(readClipboardFileText(file))
      .then((raw) => {
        addAttachment(textAttachmentFromFile(file, String(raw || "")));
      })
      .catch(() => {})
      .finally(finishRead);
  });
  if (textAttachment && remainingSlots > textFilesToRead.length) {
    addAttachment(textAttachment);
  }
  return true;
};
